import { FetchError } from "exceptions/exceptions";
import { ModelType, UploadedModelsType } from "types/ModelTypes";
import { API_URL } from "./common";
import { ProductTypeEnum } from "types/products/ProductCommandsType";
import { sha256 } from "util/FileUtils";

const BASE_URL = `${API_URL}/v1/ecommerce/models`;
const BASE_URL_V2 = `${API_URL}/v2/ecommerce/models`;

// Backend error codes returned by the v2 model upload/update endpoints
// (see docker-ecommerce-backend backend/apis/v2/ecommerce/Models.py), mapped
// to user-facing messages for toasts.
const MODEL_ERROR_MESSAGES: Record<string, string> = {
  LARGE_FILE: "The file is too large. Please upload a smaller file.",
  MISMATCHED_CHECKSUM:
    "The uploaded file appears to be corrupted. Please try uploading it again.",
  INVALID_MODEL_TYPE: "Invalid model type.",
  MISSING_FILE: "No file was provided. Please select a file to upload.",
};

// Resolves a thrown error (typically a FetchError from one of the model
// network functions below) into a user-facing toast message.
export const getModelErrorMessage = (err: unknown): string => {
  if (err instanceof FetchError && MODEL_ERROR_MESSAGES[err.message]) {
    return MODEL_ERROR_MESSAGES[err.message];
  }
  return "Something went wrong with the upload. Check the file and try again.";
};

// Parses a v2 error response body (`{"error": "CODE"}`) and throws a
// FetchError carrying the code as its message, so getModelErrorMessage can
// map it. Falls back to statusText if the body isn't the expected shape.
const throwModelFetchError = (res: Response): Promise<never> => {
  return res
    .json()
    .catch(() => null)
    .then((body: { error?: string } | null) => {
      throw new FetchError(body?.error ?? res.statusText, res.status);
    });
};

// Dummy products carry no model file to analyse, so they stay on the
// synchronous v1 endpoint; the v2 async/SSE + checksum flow is reserved for
// real uploaded model files (apiUploadModelFile / apiUpdateModelFile).
export const apiCreateModel = async (
  token: string,
  type?: ProductTypeEnum,
  file?: File,
  name?: string
): Promise<UploadedModelsType> => {
  const data = new FormData();
  if (file) {
    data.append("file", file);
    data.append("filename", file.name);
  }
  if (name) {
    data.append("name", name);
  }
  if (type) {
    data.append("type", type);
  }
  return fetch(`${BASE_URL}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
  }).then((res) => {
    if (res.ok) return res.json();
    throw new FetchError(res.statusText, res.status);
  });
};

export const apiUploadModelFile = async (
  token: string,
  file: File,
  type: ProductTypeEnum
): Promise<UploadedModelsType> => {
  const data = new FormData();
  data.append("file", file);
  data.append("filename", file.name);
  data.append("type", type);
  const checksum = await sha256(file);
  data.append("checksum", checksum);
  return fetch(`${BASE_URL_V2}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
  }).then((res) => {
    if (res.ok) return res.json();
    return throwModelFetchError(res);
  });
};

export const apiUpdateModelFile = async (
  token: string,
  file: File,
  id: string
): Promise<UploadedModelsType> => {
  const data = new FormData();
  data.append("file", file);
  data.append("filename", file.name);
  const checksum = await sha256(file);
  data.append("checksum", checksum);
  return fetch(`${BASE_URL_V2}/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: data,
  }).then((res) => {
    if (res.ok) return res.json();
    return throwModelFetchError(res);
  });
};

export const apiGetModel = (token: string, id: number): Promise<ModelType> => {
  return fetch(`${BASE_URL}/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};
